import React from 'react';
import PropTypes from 'prop-types';
import numeral from 'numeral';

import { useTheme } from 'contexts/ThemeContext';

import moment from 'utils/moment';

import { Text } from 'components/DataDisplay';
import { Container, Item } from 'components/Layout';

import { PendingChangeDiff } from './PendingChangeDiff';
import { PendingChangeLine } from './PendingChangeLine';

const formatDateRange = (start, end) => {
  const startMoment = moment(start);
  const endMoment = moment(end);
  if (!startMoment.isValid() || !endMoment.isValid()) {
    return null;
  }
  if (startMoment.isSame(endMoment, 'month')) {
    return `${startMoment.format('D')} → ${endMoment.format('D MMM')}`;
  }
  return `${startMoment.format('D MMM')} → ${endMoment.format('D MMM')}`;
};

export const PendingChangesBlock = ({ pendingChange }) => {
  const { themeColors } = useTheme();
  const { designSystem } = themeColors;
  const {
    budget,
    dates,
    geoTargeting,
    modifiedAds,
    newCreatives,
    toggledAds,
    upsertLabel,
    requestedAt
  } = pendingChange;
  const requestedLabel = moment(requestedAt).isValid()
    ? ` — demandé le ${moment(requestedAt).format('D MMM')}`
    : '';

  return (
    <Container
      flex
      style={{
        width: '100%',
        boxSizing: 'border-box',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: '8px',
        backgroundColor: designSystem.violetBg,
        border: `1px solid ${designSystem.violet}`,
        borderRadius: '9px',
        padding: '10px 12px'
      }}
    >
      <Text
        component="span"
        fontSize="11.5px"
        fontWeight={700}
        style={{ color: designSystem.violet }}
      >
        Modifications en attente
        {upsertLabel ? ` · ${upsertLabel}` : ''}
        {requestedLabel}
      </Text>
      {budget && (
        <PendingChangeLine label="Budget média">
          <PendingChangeDiff
            diff={budget}
            format={value => numeral(value).format('0,0 $')}
          />
        </PendingChangeLine>
      )}
      {dates && (
        <PendingChangeLine label="Dates de diffusion">
          <PendingChangeDiff
            diff={dates}
            format={value => formatDateRange(value.start, value.end)}
          />
        </PendingChangeLine>
      )}
      {geoTargeting && (
        <PendingChangeLine label="Ciblage géographique">
          <PendingChangeDiff diff={geoTargeting} />
        </PendingChangeLine>
      )}
      {modifiedAds.map(ad => (
        <Item
          key={ad.name}
          flex
          style={{ flexDirection: 'column', alignItems: 'stretch', gap: '4px' }}
        >
          <Text
            component="span"
            fontSize="11.5px"
            fontWeight={700}
            style={{ color: designSystem.ink }}
          >
            {ad.name}
          </Text>
          {ad.fields.map(field => (
            <PendingChangeLine key={field.key} label={field.label}>
              <PendingChangeDiff diff={field.diff} />
            </PendingChangeLine>
          ))}
        </Item>
      ))}
      {(toggledAds || []).map(ad => (
        <PendingChangeLine key={`toggle-${ad.name}`} label={ad.name}>
          <PendingChangeDiff
            diff={{
              old: ad.active ? 'En pause' : 'Active',
              new: ad.active ? 'Active' : 'En pause'
            }}
          />
        </PendingChangeLine>
      ))}
      {newCreatives.map(creative => (
        <Text
          key={creative.name}
          component="span"
          fontSize="11.5px"
          fontWeight={600}
          style={{ color: designSystem.violet }}
        >
          + Nouvelle créa {creative.name} · {creative.format}
        </Text>
      ))}
    </Container>
  );
};

PendingChangesBlock.propTypes = {
  pendingChange: PropTypes.shape().isRequired
};

export default PendingChangesBlock;
